import React, { useState, useEffect } from 'react';
import { CloudSun, Sun, Cloud, CloudRain, CloudLightning, CloudSnow, Wind, Loader } from 'lucide-react';
import './WeatherWidget.css';

// Simple mapping from QWeather icon codes to Lucide icons
export const getWeatherIcon = (iconCode, size) => {
  const code = parseInt(iconCode);
  if (code === 100 || code === 150) return <Sun size={size} />;
  if (code >= 101 && code <= 104) return <Cloud size={size} />;
  if (code >= 300 && code <= 399) return <CloudRain size={size} />;
  if (code >= 400 && code <= 499) return <CloudSnow size={size} />;
  return <CloudSun size={size} />;
};

const WeatherWidget = ({ size = '2x2', widgetScale = 1, title = '天气预报', globalWeatherData }) => {
  const loading = !globalWeatherData;

  if (loading) {
    return (
      <div className={`weather-widget medium`} style={{ padding: `${1.2 * widgetScale}rem`, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Loader className="spin" size={24 * widgetScale} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  // Use global data
  const currentTemp = globalWeatherData?.now?.temp || '28';
  const weatherText = globalWeatherData?.now?.text || '多云';
  const iconCode = globalWeatherData?.now?.icon || '101';
  
  const dailyToday = globalWeatherData?.daily?.[0];
  const maxTemp = dailyToday?.tempMax || '35';
  const minTemp = dailyToday?.tempMin || '28';

  if (size === '1x1') {
    return (
      <div className="weather-widget small" style={{ padding: `${0.5 * widgetScale}rem`, gap: `${0.3 * widgetScale}rem` }}>
        {getWeatherIcon(iconCode, 28 * widgetScale)}
        <div style={{ fontSize: `${1.1 * widgetScale}rem`, fontWeight: 600 }}>{currentTemp}°C</div>
      </div>
    );
  }

  if (size === '2x1') {
    return (
      <div className="weather-widget" style={{ 
        flexDirection: 'row', 
        padding: `${0.6 * widgetScale}rem ${0.8 * widgetScale}rem`, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: `${0.6 * widgetScale}rem`, 
        height: '100%', 
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {getWeatherIcon(iconCode, 34 * widgetScale)}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: `${6 * widgetScale}px`, whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: `${1.5 * widgetScale}rem`, fontWeight: 600, lineHeight: 1 }}>{currentTemp}°</span>
            <span style={{ fontSize: `${0.85 * widgetScale}rem`, opacity: 0.9, whiteSpace: 'nowrap' }}>{weatherText}</span>
          </div>
          <div style={{ fontSize: `${0.78 * widgetScale}rem`, opacity: 0.9, fontWeight: 500, marginTop: '3px', whiteSpace: 'nowrap' }}>
            {minTemp}°~{maxTemp}°
          </div>
        </div>
      </div>
    );
  }

  if (size === '4x2' || size === '2x4') {
    const dailyForecasts = globalWeatherData?.daily || [
      { textDay: '多云', tempMin: 28, tempMax: 35, iconDay: '101' },
      { textDay: '晴', tempMin: 29, tempMax: 36, iconDay: '100' },
      { textDay: '雷阵雨', tempMin: 27, tempMax: 33, iconDay: '302' },
      { textDay: '多云', tempMin: 28, tempMax: 34, iconDay: '101' }
    ];

    return (
      <div className="weather-widget large" style={{ padding: `${1.2 * widgetScale}rem`, display: 'flex', flexDirection: 'row', gap: `${1.5 * widgetScale}rem`, height: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: `${1.1 * widgetScale}rem`, fontWeight: 500, opacity: 0.9, whiteSpace: 'nowrap' }}>
            {title}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 'auto', marginBottom: 'auto', gap: `${0.8 * widgetScale}rem` }}>
            <div style={{ flexShrink: 0 }}>
              {getWeatherIcon(iconCode, 44 * widgetScale)}
            </div>
            <div>
              <div style={{ fontSize: `${2.6 * widgetScale}rem`, fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap' }}>
                {currentTemp}<span style={{fontSize:`${1.4 * widgetScale}rem`, verticalAlign:'super'}}>°C</span>
              </div>
            </div>
          </div>
          <div style={{ fontSize: `${0.85 * Math.max(0.7, widgetScale)}rem`, opacity: 0.95, fontWeight: 500, whiteSpace: 'nowrap' }}>
             <div style={{ whiteSpace: 'nowrap' }}>{weatherText} {minTemp}°~{maxTemp}°</div>
          </div>
        </div>
        
        <div style={{ flex: 1.4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.9 }}>
           {dailyForecasts.slice(0, 4).map((day, idx) => {
             // For a real API it might return standard dates, here we just show "今天", "明天" etc.
             const labels = ['今天', '明天', '后天', '大后天'];
             return (
               <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                 <span style={{ fontSize: `${0.85 * widgetScale}rem`, whiteSpace: 'nowrap' }}>{labels[idx]}</span>
                 {getWeatherIcon(day.iconDay, 22 * widgetScale)}
                 <span style={{ fontSize: `${0.85 * widgetScale}rem`, fontWeight: 500, whiteSpace: 'nowrap' }}>{day.tempMin}°</span>
               </div>
             );
           })}
        </div>
      </div>
    );
  }

  // Default to 2x2 or others
  return (
    <div className="weather-widget medium" style={{ padding: `${1.1 * widgetScale}rem`, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
      <h3 style={{ margin: 0, fontSize: `${1.1 * widgetScale}rem`, fontWeight: 500, opacity: 0.9, whiteSpace: 'nowrap' }}>
        {title}
      </h3>
      <div style={{ display: 'flex', alignItems: 'center', marginTop: `${1.2 * widgetScale}rem`, gap: `${0.8 * widgetScale}rem` }}>
        <div style={{ flexShrink: 0 }}>
          {getWeatherIcon(iconCode, 50 * widgetScale)}
        </div>
        <div>
          <div style={{ fontSize: `${2.6 * widgetScale}rem`, fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap' }}>
            {currentTemp}<span style={{fontSize:`${1.4 * widgetScale}rem`, verticalAlign:'super'}}>°C</span>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 'auto', fontSize: `${0.85 * Math.max(0.7, widgetScale)}rem`, opacity: 0.95, fontWeight: 500 }}>
         <div style={{ marginBottom: `${0.3 * widgetScale}rem`, whiteSpace: 'nowrap' }}>AQI {globalWeatherData?.now?.precip > 0 ? '良' : '优'}</div>
         <div style={{ whiteSpace: 'nowrap' }}>{weatherText} {minTemp}°~{maxTemp}°</div>
      </div>
    </div>
  );
};

export default WeatherWidget;
